import { NotificationType, type types } from "@fraction/shared";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMatch } from "react-router-dom";
import fraction from "src/api/fraction";
import { ChatModal } from "src/components/ChatModal";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";
import { useNotificationPreferences } from "src/hooks/useNotificationPreferences";
import { isMainWindow } from "src/lib/runtime/environment";
import { useEmailComposeSessionsStore } from "src/stores/emailComposeSessionsStore";
import { type StoredNotification, useNotificationStore } from "src/stores/notificationStore";

import { ChatMessageCard } from "./cards/ChatMessageCard";
import { CommitmentLetterRateMismatchCard } from "./cards/CommitmentLetterRateMismatchCard";
import { EmailReceivedCard, type ReplyToEmailArgs } from "./cards/EmailReceivedCard";
import { StatusChangeCard } from "./cards/StatusChangeCard";
import { WorkflowConfirmationCard } from "./cards/WorkflowConfirmationCard";
import { isHiddenFromNonEmployees } from "./notificationAudience";
import { broadcastDismissAll, broadcastDismissMany } from "./notificationChannel";

const MAX_GROUPS = 5;
const MAX_STACK = 5;

interface NotificationGroup {
  key: string;
  items: StoredNotification[];
  allIds: string[];
}

function SingleCard({
  stored,
  onDismiss,
  onReplyToEmail,
  count,
}: {
  stored: StoredNotification;
  onDismiss: () => void;
  onReplyToEmail: (args: ReplyToEmailArgs) => void;
  count?: number;
}) {
  const { notification } = stored;

  return (
    <div className="pr-6">
      {notification.type === NotificationType.EMAIL_RECEIVED && (
        <EmailReceivedCard
          notification={notification}
          onDismiss={onDismiss}
          onReplyToEmail={onReplyToEmail}
          count={count}
        />
      )}
      {notification.type === NotificationType.CHAT_MESSAGE_RECEIVED && (
        <ChatMessageCard notification={notification} onDismiss={onDismiss} count={count} />
      )}
      {notification.type === NotificationType.APPLICATION_STATUS_CHANGE && (
        <StatusChangeCard notification={notification} onDismiss={onDismiss} count={count} />
      )}
      {notification.type === NotificationType.WORKFLOW_CONFIRMATION_REQUEST && (
        <WorkflowConfirmationCard notification={notification} onDismiss={onDismiss} count={count} />
      )}
      {notification.type === NotificationType.COMMITMENT_LETTER_RATE_MISMATCH && (
        <CommitmentLetterRateMismatchCard notification={notification} onDismiss={onDismiss} count={count} />
      )}
    </div>
  );
}

function NotificationStack({
  group,
  onDismissGroup,
  onDismissSingle,
  onReplyToEmail,
}: {
  group: NotificationGroup;
  onDismissGroup: (ids: string[]) => void;
  onDismissSingle: (ids: string[]) => void;
  onReplyToEmail: (args: ReplyToEmailArgs) => void;
}) {
  const items = group.items;
  const isStack = items.length > 1;

  const handleDismissAll = useCallback(() => {
    onDismissGroup(group.allIds);
  }, [onDismissGroup, group.allIds]);

  const handleDismissSingle = useCallback(
    (id: string) => {
      onDismissSingle([id]);
    },
    [onDismissSingle]
  );

  if (!isStack) {
    const stored = items[0];
    return (
      <div className="relative w-[360px] rounded-xl border border-border bg-card p-3 shadow-lg">
        <button
          type="button"
          onClick={handleDismissAll}
          className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-muted-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <SingleCard stored={stored} onDismiss={handleDismissAll} onReplyToEmail={onReplyToEmail} />
      </div>
    );
  }

  return (
    <div className="group/stack relative">
      {/* Collapsed: grid overlaps all cards in one cell; expanded: flex spreads them */}
      <div className="grid grid-cols-1 grid-rows-1 group-hover/stack:flex group-hover/stack:flex-col-reverse group-hover/stack:gap-2">
        <div className="hidden self-end text-xs  group-hover/stack:block  text-foreground/80 transition-colors hover:text-foreground bg-card px-2 py-0.5 rounded-full">
          <button type="button" onClick={handleDismissAll}>
            Dismiss group ({items.length})
          </button>
        </div>
        {items.map((stored, i) => {
          const dismissThis = () => handleDismissSingle(stored.notification.id);

          return (
            <div
              key={stored.notification.id}
              className={`
                relative w-[360px] rounded-xl border border-border bg-card p-3 shadow-lg
                transition-[transform,opacity] duration-300 ease-out
                ${i > 0 ? "translate-y-2 scale-[0.97] opacity-0 pointer-events-none group-hover/stack:translate-y-0 group-hover/stack:scale-100 group-hover/stack:opacity-100 group-hover/stack:pointer-events-auto" : ""}
              `}
              style={{
                gridRow: 1,
                gridColumn: 1,
                zIndex: items.length - i,
                transformOrigin: "bottom right",
              }}
            >
              <button
                type="button"
                onClick={dismissThis}
                className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-muted hover:text-muted-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <SingleCard
                stored={stored}
                onDismiss={dismissThis}
                onReplyToEmail={onReplyToEmail}
                count={i === 0 && isStack ? group.allIds.length : undefined}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function NotificationCenter() {
  const allNotifications = useNotificationStore((s) => s.notifications);
  const storeDismissMany = useNotificationStore((s) => s.dismissMany);
  const storeDismissAll = useNotificationStore((s) => s.dismissAll);
  const getServerNotificationIds = useNotificationStore((s) => s.getServerNotificationIds);
  const getAllServerNotificationIds = useNotificationStore((s) => s.getAllServerNotificationIds);
  const appMatch = useMatch("/app/:id/*");
  const queryClient = useQueryClient();
  const { preference } = useAccountViewPreference();
  const isEmployee = preference === "employee";

  // Remove chat notifications from the store when the chat panel is already open
  useEffect(() => {
    const currentAppId = appMatch?.params.id;
    if (!currentAppId) return;

    const idsToRemove = allNotifications
      .filter((stored) => {
        if (stored.notification.type !== NotificationType.CHAT_MESSAGE_RECEIVED) return false;
        const chatNotification = stored.notification as types.ChatMessageNotification;
        if (chatNotification.applicationId !== currentAppId) return false;
        const cached = queryClient.getQueryData<{ data: boolean }>([
          "cacheItems",
          `app-chat-expanded-${chatNotification.chatType}`,
        ]);
        return cached?.data === true;
      })
      .map((stored) => stored.notification.id);

    if (idsToRemove.length > 0) {
      const serverIds = getServerNotificationIds(idsToRemove);
      storeDismissMany(idsToRemove);
      broadcastDismissMany(idsToRemove);
      if (serverIds.length > 0) {
        fraction.markNotificationsRead(serverIds).catch(() => {});
      }
    }
  }, [allNotifications, appMatch?.params.id, queryClient, storeDismissMany, getServerNotificationIds]);

  const dismissIds = useCallback(
    (ids: string[]) => {
      const serverIds = getServerNotificationIds(ids);
      storeDismissMany(ids);
      broadcastDismissMany(ids);
      if (serverIds.length > 0) {
        fraction.markNotificationsRead(serverIds).catch(() => {});
      }
    },
    [storeDismissMany, getServerNotificationIds]
  );

  const dismissAll = useCallback(() => {
    const serverIds = getAllServerNotificationIds();
    storeDismissAll();
    broadcastDismissAll();
    if (serverIds.length > 0) {
      fraction.markAllNotificationsRead().catch(() => {});
    }
  }, [storeDismissAll, getAllServerNotificationIds]);

  const openCompose = useEmailComposeSessionsStore((s) => s.openCompose);

  const handleReplyToEmail = useCallback(
    ({
      communicationId,
      applicationId,
      applicationEid,
      applicantNames,
      subject,
      senderEmail,
    }: ReplyToEmailArgs) => {
      // Open with replyToCommunicationId only — the modal fetches the full comm
      // and derives the draft from it. Passing a stub `replyTo` here would freeze
      // the contextKey on its id, blocking the post-fetch draft re-derivation.
      // initialTo/initialSubject act as placeholders so the modal isn't blank
      // while the comm fetch is in flight; the post-fetch derive overwrites them.
      const seedSubject = subject ? (subject.startsWith("Re:") ? subject : `Re: ${subject}`) : undefined;
      openCompose({
        mode: "replyAll",
        applicationId,
        applicationEid,
        applicantNames,
        replyToCommunicationId: communicationId,
        initialTo: senderEmail ? [senderEmail] : undefined,
        initialSubject: seedSubject,
      });
    },
    [openCompose]
  );

  // Group notifications by type + applicationId, keeping up to MAX_STACK per group
  const groups = useMemo(() => {
    const groupMap = new Map<string, NotificationGroup>();

    for (const stored of allNotifications) {
      // Server-loaded rows bypass the WS-side filter, so re-apply it here.
      if (!isEmployee && isHiddenFromNonEmployees(stored.notification)) {
        continue;
      }
      const { type, applicationId } = stored.notification;
      const key = `${type}:${applicationId}`;

      const existing = groupMap.get(key);
      if (existing) {
        existing.allIds.push(stored.notification.id);
        if (existing.items.length < MAX_STACK) {
          existing.items.push(stored);
        }
      } else {
        groupMap.set(key, {
          key,
          items: [stored],
          allIds: [stored.notification.id],
        });
      }
    }

    // Sort items within each group by timestamp descending
    for (const group of groupMap.values()) {
      group.items.sort((a, b) => b.notification.timestamp - a.notification.timestamp);
    }

    // Sort groups by most recent notification
    return Array.from(groupMap.values()).sort(
      (a, b) => b.items[0].notification.timestamp - a.items[0].notification.timestamp
    );
  }, [allNotifications, isEmployee]);

  const visibleGroups = useMemo(() => groups.slice(0, MAX_GROUPS), [groups]);
  const totalCount = useMemo(() => groups.reduce((sum, g) => sum + g.allIds.length, 0), [groups]);

  // Presentation-only opt-out (Settings → Notifications). The websocket
  // subscription and the store are untouched, so notifications still arrive and
  // still mark as read elsewhere — they just don't pop over the UI.
  const { showCards } = useNotificationPreferences();
  // Cards belong to the main window alone. A detached window is opened to do one thing — read this
  // document, talk to this agent — and the same deal notifications stacking into every one of them
  // buries that, several times over. The store and the read-marking are shared, so dismissing a
  // card in the main window still settles it everywhere.
  const cardsVisible = showCards && isMainWindow();

  // Tell the backend which cards the user actually had in front of them, so the delayed fallback
  // email is skipped for those (an open background tab alone doesn't count). A stack's top card
  // shows the group's count, so the whole group counts as seen.
  const markedSeenIds = useRef(new Set<string>());
  useEffect(() => {
    if (!cardsVisible) return;
    const markOnScreenSeen = () => {
      if (document.visibilityState !== "visible" || !document.hasFocus()) return;
      const ids = visibleGroups.flatMap((g) => g.allIds).filter((id) => !markedSeenIds.current.has(id));
      if (!ids.length) return;
      for (const id of ids) markedSeenIds.current.add(id);
      const serverIds = getServerNotificationIds(ids);
      if (serverIds.length > 0) {
        fraction.markNotificationsSeen(serverIds).catch(() => {});
      }
    };
    markOnScreenSeen();
    window.addEventListener("focus", markOnScreenSeen);
    document.addEventListener("visibilitychange", markOnScreenSeen);
    return () => {
      window.removeEventListener("focus", markOnScreenSeen);
      document.removeEventListener("visibilitychange", markOnScreenSeen);
    };
  }, [cardsVisible, visibleGroups, getServerNotificationIds]);

  return (
    <>
      <ChatModal />

      {cardsVisible && groups.length > 0 && (
        <div className="pointer-events-none fixed inset-0 z-50 flex flex-col items-end justify-end p-4">
          <div className="pointer-events-auto flex flex-col-reverse gap-2">
            {totalCount > 1 && (
              <button
                type="button"
                onClick={dismissAll}
                className="self-end text-xs text-foreground/80 transition-colors hover:text-foreground bg-card px-2 py-0.5 rounded-full"
              >
                Dismiss all ({totalCount})
              </button>
            )}

            {visibleGroups.map((group) => (
              <NotificationStack
                key={group.key}
                group={group}
                onDismissGroup={dismissIds}
                onDismissSingle={dismissIds}
                onReplyToEmail={handleReplyToEmail}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}
